import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';
import './Auth.css';

export default function SignIn({ onSwitchToForgotPassword, from }) {
  const { signIn, isLoading, error, clearError, isAuthenticated, signInWithGoogle } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Redirect to app when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Preserve the full URL the user was trying to reach, including the
      // query string — otherwise OAuth callbacks (?code=…&state=…) lose
      // their params when the session expired mid-flow and the user has
      // to re-authenticate before completing the redirect.
      const fromLoc = from || location.state?.from;
      const destination = fromLoc
        ? `${fromLoc.pathname || ''}${fromLoc.search || ''}${fromLoc.hash || ''}` || '/app'
        : '/app';
      navigate(destination, { replace: true });
    }
  }, [isAuthenticated, navigate, from, location.state]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Clear auth error when user starts typing
    if (error) {
      clearError();
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.username.trim()) {
      errors.username = 'Email is required';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await signIn(formData.username, formData.password);
      // Show success message briefly before redirect
      setShowSuccess(true);
    } catch (err) {
      // Error is already set in the auth context
      console.error('Sign in error:', err);
    }
  };

  const getFieldError = (fieldName) => {
    return validationErrors[fieldName] || '';
  };

  const handleSignInWithGoogle = () => {
    signInWithGoogle();
  } 

  return (
    <div className="auth-container">
      <div className="auth-header">
        <h2>Sign In</h2>
        <p>Access your OmniContext account</p>
      </div>

      <form onSubmit={handleSubmit} className="auth-form">
        <div className="form-group">
          <label htmlFor="username">Email</label>
          <input
            type="text"
            id="username"
            name="username"
            value={formData.username}
            onChange={handleInputChange}
            className={getFieldError('username') ? 'error' : ''}
            placeholder="Enter your email"
            disabled={isLoading}
          />
          {getFieldError('username') && (
            <span className="error-message">{getFieldError('username')}</span>
          )}
        </div>

        <div className="form-group has-toggle">
          <label htmlFor="password">Password</label>
          <div className="password-input-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={getFieldError('password') ? 'error' : ''}
              placeholder="Enter your password"
              disabled={isLoading}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              disabled={isLoading}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <i className={showPassword ? "fas fa-eye-slash" : "fas fa-eye"}></i>
            </button>
          </div>
          {getFieldError('password') && (
            <span className="error-message">{getFieldError('password')}</span>
          )}
        </div>

        {error && (
          <div className="auth-error">
            <i className="fas fa-exclamation-triangle"></i>
            <span>{error}</span>
          </div>
        )}

        {showSuccess && (
          <div className="auth-success">
            <i className="fas fa-check-circle"></i>
            <span>Login successful! Redirecting...</span>
          </div>
        )}

        <button
          type="submit"
          className="auth-button primary"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <div className="loading-spinner-auth">
                <div className="spinner-ring"></div>
                <div className="spinner-ring"></div>
                <div className="spinner-ring"></div>
              </div>
              Signing in...
            </>
          ) : showSuccess ? (
            <>
              <i className="fas fa-check"></i>
              Success!
            </>
          ) : (
            <>
              <i className="fas fa-sign-in-alt"></i>
              Sign In
            </>
          )}
        </button>
        
      </form>

      <div className="auth-divider">
        <span>or</span>
      </div>

      <button
        type="button"
        onClick={handleSignInWithGoogle}
        className="gsi-material-button"
        disabled={isLoading}
      >
        <div className="gsi-material-button-state"></div>
        <div className="gsi-material-button-content-wrapper">
          <div className="gsi-material-button-icon">
            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" style={{ display: 'block' }}>
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
              <path fill="none" d="M0 0h48v48H0z"></path>
            </svg>
          </div>
          <span className="gsi-material-button-contents">Sign in with Google</span>
        </div>
      </button>

      <div className="auth-links">
        <button
          type="button"
          className="link-button"
          onClick={onSwitchToForgotPassword}
        >
          Forgot password?
        </button>
      </div>
    </div>
  );
}
